import { Component, type ErrorInfo, type ReactNode } from 'react'
import { Async } from './Async'

/**
 * Contains a render-time throw — #95.
 *
 * `Money` throws on non-integer cents and `DataGrid` throws on a subtotal its
 * own rows contradict. Both are right: a plausible wrong number is worse than a
 * named failure, because nobody audits a figure that looks correct. But without
 * a boundary React unwinds to the root, and web-tester measured what that
 * actually costs on `DataGrid` — `root.children.length` 1 → 0, a blank
 * document, every control on the page gone, unrecoverable without a reload,
 * with the only signal in the console.
 *
 * **Per panel, not per route.** #95 asks where these sit and the five-state
 * contract answers it: every list, chart and panel already promises an error
 * state, so one broken widget must not take its page down. A route-level
 * boundary would turn a broken Cashflow tile into a blank Overview.
 *
 * **Routed through `Async`'s error state** rather than given its own panel, so
 * a crash looks like every other failure in the app and there is one place that
 * owns error copy and layout.
 *
 * A class component because React exposes no hook for this; `getDerivedState-
 * FromError` and `componentDidCatch` have no function equivalent.
 */

type Props = {
  children: ReactNode
  /** Names the failing section, so the message can say what broke. */
  name: string
  /** Injected so the action is testable; defaults to a real page reload. */
  onReload?: () => void
  /**
   * Overrides the recovery entirely.
   *
   * The default is Reload, and on a PANEL boundary that is right — a
   * transiently bad response is refetched. On a LAYOUT boundary it is weaker
   * than it looks: a reload re-renders the same route against the same data and
   * throws identically. There the action that reliably works is leaving, which
   * is what the layouts' `key={pathname}` already clears state for.
   */
  recovery?: { label: string; onAct: () => void }
  /**
   * Rendered instead of the error panel when the failing subtree is decorative.
   *
   * `AppTexture` runs generative code — `createElementNS`, a seeded PRNG,
   * `buildHexMeshDataUri` which throws on a bad seed — at app root, above the
   * router and therefore outside every layout boundary. A throw there took the
   * whole app down for a background layer. But an error PANEL is the wrong
   * answer for decoration: the right failure is to render nothing and let the
   * app carry on without its texture.
   */
  fallback?: ReactNode
}

type State = { error: Error | null }

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // The diagnostic goes to the log, not to the screen. `DataGrid`'s message
    // carries amounts and `Money`'s carries a value — CLAUDE.md keeps those out
    // of what we transmit, and #112 will wire a reporter that ingests exactly
    // these strings, so what is user-facing and what is diagnostic have to stay
    // separate from the start.
    console.error(`ErrorBoundary caught in "${this.props.name}":`, error, info.componentStack)
  }

  override render(): ReactNode {
    if (!this.state.error) return this.props.children
    if (this.props.fallback !== undefined) return this.props.fallback

    return (
      <Async
        surface="error-boundary"
        state={{
          status: 'error',
          /**
           * Names the section and owns the fault.
           *
           * CLAUDE.md: an error says whose fault and the recovery path, and
           * "Something went wrong" is a defect where something specific is
           * possible. Here something specific IS possible — we know which
           * section failed, and we know it is our bug rather than the user's
           * data or their bank, because everything reaching a render-time
           * invariant has already passed `parseMoney` at the API boundary.
           */
          message: `${this.props.name} couldn't be displayed. That's a bug on our side, not a problem with your data or your bank.`,
          /**
           * Reload, not Retry.
           *
           * A component that throws while rendering throws again on the same
           * props — retrying the render is the one action guaranteed not to
           * work. This is the distinction #95 asks for between an invariant
           * violation and a network failure: they look similar and their
           * recovery paths are opposites.
           */
          recovery: this.props.recovery ?? {
            label: 'Reload the page',
            onAct: this.props.onReload ?? (() => window.location.reload()),
          },
        }}
        // Structurally required by `Async` and unreachable from an error state.
        // Passing them is the cost of having one component own what a failure
        // looks like, which is worth more than the noise.
        skeleton={null}
        empty={{ title: '' }}
      >
        {() => null}
      </Async>
    )
  }
}
